// src/pages/OrderViewPage.js — KDS (Kitchen Display System)
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage, translateDynamic, getTransliteration } from '../../context/LanguageContext';
import { format, parseISO } from 'date-fns';
import { orderTrackingService, categoryService, voidService } from '../../services/api';
import { renderEntityImage } from '../../utils/imageUtils';
import dedupeByName from '../../utils/dedupeByName';
import tenantStorage from '../../services/tenantStorage';
import useBusinessDayGuard from '../../hooks/pos/useBusinessDayGuard';

// ── KDS Light Theme Tokens ─────────────────────────────────────────
const K = {
    bg:          '#f8fafc',
    card:        '#ffffff',
    border:      '#e2e8f0',
    text:        '#1e293b',
    muted:       '#64748b',
    dimmed:      '#94a3b8',
    green:       '#22c55e',
    greenDim:    '#dcfce7',
    greenText:   '#16a34a',
    greenDark:   '#052e16',
    yellow:      '#eab308',
    yellowText:  '#a16207',
    red:         '#ef4444',
    redText:     '#dc2626',
    redGlow:     '#ef444422',
    blue:        '#3b82f6',
    blueDim:     '#dbeafe',
    purple:      '#7c3aed',
    purpleDim:   '#ede9fe',
    orange:      '#ea580c',
    amberDim:    '#fef3c7',
    amberBorder: '#fde68a',
    amberText:   '#92400e',
};

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";

// ── Default timer thresholds (minutes) ─────────────────────────────
const DEFAULT_THRESHOLDS = { warning: 10, critical: 15 };

// ── Helpers ────────────────────────────────────────────────────────
// GCP stores timestamps in UTC but may omit the Z suffix — normalize so parseISO treats them as UTC
const normalizeUtc = (ts) => {
    if (!ts) return ts;
    if (/[Zz]$/.test(ts) || /[+-]\d{2}:\d{2}$/.test(ts)) return ts;
    return ts + 'Z';
};

const getElapsedMinutes = (submittedAt) => {
    if (!submittedAt) return 0;
    try {
        const submitted = parseISO(normalizeUtc(submittedAt));
        return Math.floor((Date.now() - submitted.getTime()) / 60000);
    } catch { return 0; }
};

const getElapsedSeconds = (submittedAt) => {
    if (!submittedAt) return 0;
    try {
        const submitted = parseISO(normalizeUtc(submittedAt));
        return Math.max(0, Math.floor((Date.now() - submitted.getTime()) / 1000));
    } catch { return 0; }
};

const formatTimer = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const getUrgency = (elapsedMinutes, thresholds) => {
    if (elapsedMinutes >= thresholds.critical) return 'critical';
    if (elapsedMinutes >= thresholds.warning) return 'warning';
    return 'fresh';
};

const urgencyColors = {
    fresh:    { bar: K.green,  text: K.greenText,  dot: K.green,  pulse: '1.5s', glow: 'none' },
    warning:  { bar: K.yellow, text: K.yellowText,  dot: K.yellow, pulse: '1.5s', glow: 'none' },
    critical: { bar: K.red,    text: K.redText,     dot: K.red,    pulse: '0.8s', glow: `0 0 12px ${K.redGlow}` },
};

// ── Grid column calculation ────────────────────────────────────────
const getGridColumns = (count) => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return 1;
    if (count <= 6) return 3;
    if (count <= 12) return 4;
    if (count <= 20) return 5;
    return 6;
};

// ── Completed Tab Component ────────────────────────────────────────
const CompletedTab = ({ orders, thresholds, t, language, windowWidth }) => {
    // Stats
    const completedCount = orders.length;
    const totalItems = orders.reduce((sum, o) => sum + (o.total_items_count || 0), 0);
    const prepTimes = orders.map(o => {
        if (!o.submitted_at || !o.completed_at) return null;
        try {
            return Math.floor((parseISO(o.completed_at) - parseISO(o.submitted_at)) / 60000);
        } catch { return null; }
    }).filter(t => t !== null);
    const avgPrep = prepTimes.length > 0 ? (prepTimes.reduce((a, b) => a + b, 0) / prepTimes.length).toFixed(1) : '\u2014';
    const overThreshold = prepTimes.filter(t => t >= thresholds.critical).length;

    const stats = [
        { value: completedCount, label: t('kds_completed_today') },
        { value: `${avgPrep}m`, label: t('kds_avg_prep_time') },
        { value: totalItems, label: t('kds_items_prepared') },
        { value: overThreshold, label: t('kds_over_threshold') },
    ];

    return (
        <div style={{ height: '100%', overflow: 'auto' }}>
            {/* Summary strip */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: windowWidth < 768 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                gap: windowWidth < 768 ? 8 : 16,
                marginBottom: 16, padding: '12px 16px',
                background: K.card, borderRadius: 10,
            }}>
                {stats.map((s, i) => (
                    <div key={i} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: windowWidth < 768 ? 20 : 24, fontWeight: 800, color: K.greenText }}>{s.value}</div>
                        <div style={{
                            fontSize: 11, color: K.dimmed, textTransform: 'uppercase', letterSpacing: 0.5,
                        }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Completed grid */}
            {orders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: K.dimmed }}>
                    {t('kds_no_active_orders')}
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: windowWidth < 768 ? '1fr' : 'repeat(4, 1fr)', gap: 10 }}>
                    {orders.map(order => {
                        const prepTime = order.submitted_at && order.completed_at
                            ? Math.floor((parseISO(order.completed_at) - parseISO(order.submitted_at)) / 60000)
                            : null;
                        const itemSummary = (order.items || [])
                            .map(i => `${i.quantity}x ${getTransliteration(i.name, language, t)}`)
                            .join(', ');

                        return (
                            <div key={order.tracking_id} style={{
                                background: K.card, borderRadius: 10,
                                borderLeft: `4px solid ${K.green}`, padding: 12, opacity: 0.7,
                            }}>
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'center', marginBottom: 6,
                                }}>
                                    <span style={{ fontWeight: 700, fontSize: 13, color: K.muted }}>
                                        {translateDynamic(order.table_info?.dining_option || '', t)}{' '}
                                        #{order.table_info?.table_number || order.order_id || '?'}
                                    </span>
                                    <span style={{ fontSize: 11, color: K.dimmed }}>
                                        {order.completed_at ? format(parseISO(order.completed_at), 'HH:mm') : ''}
                                    </span>
                                </div>
                                <div style={{ fontSize: 10, color: K.dimmed, marginBottom: 4 }}>
                                    {order.tracking_id}
                                </div>
                                <div style={{ fontSize: 11, color: K.dimmed, lineHeight: 1.6, wordBreak: 'break-word' }}>
                                    {itemSummary}
                                </div>
                                <div style={{
                                    display: 'flex', gap: 12, marginTop: 8, paddingTop: 8,
                                    borderTop: `1px solid ${K.border}`,
                                }}>
                                    {prepTime !== null && (
                                        <span style={{ fontSize: 10, color: K.dimmed }}>
                                            {t('kds_prep_time')}: <strong style={{ color: K.muted }}>{prepTime}m</strong>
                                        </span>
                                    )}
                                    <span style={{ fontSize: 10, color: K.dimmed }}>
                                        {t('kds_items')}: <strong style={{ color: K.muted }}>{order.total_items_count}</strong>
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ── Voided Tab Component ──────────────────────────────────────────
const VoidedTab = ({ voidedOrders, t, language, windowWidth }) => {
    return (
        <div style={{ height: '100%', overflow: 'auto' }}>
            {/* Summary strip */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: windowWidth < 768 ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)',
                gap: windowWidth < 768 ? 8 : 16,
                marginBottom: 16, padding: '12px 16px',
                background: K.card, borderRadius: 10,
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: windowWidth < 768 ? 20 : 24, fontWeight: 800, color: K.redText }}>{voidedOrders.length}</div>
                    <div style={{ fontSize: 11, color: K.dimmed, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('kds_voided_today')}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: windowWidth < 768 ? 20 : 24, fontWeight: 800, color: K.redText }}>
                        {voidedOrders.reduce((sum, v) => sum + ((v.record_data?.items || []).length || v.record_data?.total_items_count || 0), 0)}
                    </div>
                    <div style={{ fontSize: 11, color: K.dimmed, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('kds_items_voided')}</div>
                </div>
            </div>

            {voidedOrders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: K.dimmed }}>
                    {t('kds_no_voided_orders')}
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: windowWidth < 768 ? '1fr' : 'repeat(4, 1fr)', gap: 10 }}>
                    {voidedOrders.map(v => {
                        const rd = v.record_data || {};
                        const items = rd.items || [];
                        const tableInfo = rd.table_info || {};
                        const itemSummary = items.length > 0
                            ? items.map(i => `${i.quantity || 1}x ${getTransliteration(i.name || i.product_name || 'Item', language, t)}`).join(', ')
                            : `${rd.total_items_count || 0} items`;

                        return (
                            <div key={v.void_id} style={{
                                background: K.card, borderRadius: 10,
                                borderLeft: `4px solid ${K.red}`, padding: 12,
                                position: 'relative', overflow: 'hidden',
                            }}>
                                {/* VOIDED stamp */}
                                <div style={{
                                    position: 'absolute', top: '50%', left: '50%',
                                    transform: 'translate(-50%, -50%) rotate(-18deg)',
                                    fontSize: 28, fontWeight: 900, color: K.red,
                                    opacity: 0.15, letterSpacing: 4, pointerEvents: 'none',
                                    fontFamily: FONT, textTransform: 'uppercase',
                                }}>
                                    {t('voided') || 'VOIDED'}
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                    <span style={{ fontWeight: 700, fontSize: 13, color: K.redText }}>
                                        {translateDynamic(tableInfo.dining_option || rd.category || '', t)}{' '}
                                        #{tableInfo.table_number || rd.order_id || '?'}
                                    </span>
                                    <span style={{ fontSize: 11, color: K.dimmed }}>
                                        {v.deleted_at ? format(new Date(v.deleted_at), 'HH:mm') : ''}
                                    </span>
                                </div>
                                <div style={{ fontSize: 10, color: K.dimmed, marginBottom: 4 }}>
                                    {rd.tracking_id || v.record_id}
                                </div>
                                <div style={{ fontSize: 11, color: K.dimmed, lineHeight: 1.6, wordBreak: 'break-word' }}>
                                    {itemSummary}
                                </div>
                                <div style={{
                                    display: 'flex', gap: 12, marginTop: 8, paddingTop: 8,
                                    borderTop: `1px solid ${K.border}`,
                                }}>
                                    <span style={{ fontSize: 10, color: K.dimmed }}>
                                        {t('voids_deleted_by')}: <strong style={{ color: K.redText }}>{v.authorized_by}</strong>
                                    </span>
                                    {v.reason && (
                                        <span style={{ fontSize: 10, color: K.dimmed, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>
                                            {v.reason}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ── Undo Bar Component ─────────────────────────────────────────────
const UndoBar = ({ onUndo, t }) => (
    <div style={{ padding: '8px 14px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                    width: 24, height: 24, borderRadius: '50%', background: K.green,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke={K.greenDark} strokeWidth="3"
                         strokeLinecap="round" width="14" height="14">
                        <path d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <span style={{ color: K.greenText, fontSize: 13, fontWeight: 600 }}>
                    {t('kds_completed_label')}
                </span>
            </div>
            <button onClick={onUndo} style={{
                background: 'none', border: `1px solid ${K.yellowText}66`,
                color: K.yellowText, padding: '6px 16px', borderRadius: 6,
                fontWeight: 700, fontSize: 13, cursor: 'pointer', flexShrink: 0,
                transition: 'all 0.15s',
            }}>
                {t('kds_undo')}
            </button>
        </div>
        <div style={{
            height: 3, background: `${K.green}44`, borderRadius: 2, marginTop: 8, overflow: 'hidden',
        }}>
            <div style={{
                height: '100%', background: K.green, borderRadius: 2,
                animation: 'kdsUndoBar 3s linear forwards',
            }} />
        </div>
    </div>
);

// ── Order Card Component ───────────────────────────────────────────
const OrderCard = ({ order, thresholds, isNew, isCompleting, onDone, onUndo, language, t, compact }) => {
    // A refund cancels the ticket rather than deleting it. It STAYS on the
    // board, struck through, because a card that silently disappears gives a
    // cook mid-order no signal to stop — that is the whole reason
    // cancellation exists instead of deletion.
    const isCancelled = order.status === 'cancelled';
    const elapsed = getElapsedSeconds(order.submitted_at);
    const elapsedMin = Math.floor(elapsed / 60);
    const urgency = getUrgency(elapsedMin, thresholds);
    const colors = urgencyColors[urgency];

    const cardStyle = {
        background: isCompleting ? K.greenDark : K.card,
        borderRadius: 12,
        color: K.text,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: colors.glow,
        transition: 'all 0.3s ease',
        animation: isNew ? 'kdsSlideIn 0.3s ease, kdsGlow 2s ease' : undefined,
        opacity: isCancelled ? 0.6 : (isCompleting ? 0.85 : 1),
        position: isCancelled ? 'relative' : undefined,
    };

    return (
        <div style={cardStyle}>
            {isCancelled && (
                <div
                    aria-hidden="true"
                    style={{
                        position: 'absolute', inset: 0, zIndex: 2,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        // Never eat a tap: the cards behind it stay usable.
                        pointerEvents: 'none',
                    }}
                >
                    <span style={{
                        transform: 'rotate(-14deg)',
                        border: `3px solid rgba(220,38,38,0.55)`,
                        color: 'rgba(220,38,38,0.75)',
                        borderRadius: 8,
                        padding: compact ? '4px 12px' : '6px 18px',
                        fontSize: compact ? 16 : 24,
                        fontWeight: 900,
                        letterSpacing: 2,
                        textTransform: 'uppercase',
                        background: 'rgba(255,255,255,0.06)',
                        whiteSpace: 'nowrap',
                    }}>
                        {t('kds_cancelled')}
                    </span>
                </div>
            )}
            <div style={{
                display: 'flex', flex: 1,
                textDecoration: isCancelled ? 'line-through' : undefined,
            }}>
                {/* Urgency bar */}
                <div style={{
                    width: 4, flexShrink: 0,
                    background: isCompleting ? K.green : colors.bar,
                    borderRadius: '12px 0 0 12px',
                }} />

                {/* Card content */}
                <div style={{
                    flex: 1, padding: compact ? '8px 10px' : '12px 14px',
                    opacity: isCompleting ? 0.4 : 1,
                    transition: 'opacity 0.3s',
                }}>
                    {/* Header: table info + timer */}
                    <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', marginBottom: compact ? 6 : 10,
                    }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 800, fontSize: compact ? 12 : 15 }}>
                                    {translateDynamic(order.table_info?.dining_option || 'unknown', t)}{' '}
                                    #{order.table_info?.table_number || order.order_id || order.tracking_id?.slice(-4).toUpperCase() || '?'}
                                </span>
                                {order.is_edited && (
                                    <span style={{
                                        backgroundColor: K.amberDim,
                                        color: K.yellowText,
                                        padding: '2px 8px',
                                        borderRadius: '9999px',
                                        fontSize: 11,
                                        fontWeight: 600,
                                    }}>
                                        {t('updated_order')}
                                    </span>
                                )}
                            </div>
                            <div style={{ fontSize: 10, color: K.dimmed, marginTop: 2 }}>
                                {order.table_info?.staff_name && (
                                    <span style={{ color: K.muted, fontWeight: 600 }}>{order.table_info.staff_name} &middot; </span>
                                )}
                                {order.tracking_id}
                                {order.session_id && <span> &middot; {order.session_id}</span>}
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <div style={{
                                width: 6, height: 6, borderRadius: '50%',
                                background: colors.dot,
                                animation: `kdsPulse ${colors.pulse} infinite`,
                            }} />
                            <span style={{
                                fontFamily: 'monospace', fontSize: compact ? 11 : 13,
                                color: colors.text, fontWeight: 700,
                            }}>
                                {formatTimer(elapsed)}
                            </span>
                        </div>
                    </div>

                    {/* Items list */}
                    <div style={{ borderTop: `1px solid ${K.border}`, paddingTop: compact ? 6 : 10 }}>
                        {order.items?.map((item, idx) => (
                            <div key={idx} style={{ marginBottom: compact ? 4 : 8 }}>
                                {/* Item row */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                    <span style={{
                                        background: K.blueDim, color: '#60a5fa',
                                        minWidth: compact ? 28 : 32, height: compact ? 28 : 32,
                                        borderRadius: '50%', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', fontSize: compact ? 14 : 16,
                                        fontWeight: 700, flexShrink: 0,
                                    }}>
                                        {item.quantity}
                                    </span>
                                    <span style={{ fontSize: compact ? 11 : 13, fontWeight: 600, wordBreak: 'break-word', minWidth: 0 }}>
                                        {getTransliteration(item.name, language, t)}
                                    </span>
                                </div>

                                {/* Add-ons */}
                                {item.add_ons?.length > 0 && (
                                    <div style={{ marginLeft: compact ? 24 : 28, marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                        {item.add_ons.map((addon, ai) => (
                                            <span key={ai} style={{
                                                color: K.purple, fontSize: compact ? 10 : 11,
                                                background: K.purpleDim, padding: '2px 6px',
                                                borderRadius: 3,
                                            }}>
                                                + {addon.name || addon}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Item notes */}
                                {item.notes && (
                                    <div style={{
                                        marginLeft: compact ? 24 : 28, marginTop: 3,
                                        color: K.orange, fontSize: compact ? 10 : 11, fontStyle: 'italic',
                                    }}>
                                        {item.notes}
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Order-level note */}
                        {order.notes && (
                            <div style={{
                                marginTop: compact ? 6 : 10,
                                background: K.amberDim,
                                border: `1px solid ${K.amberBorder}`,
                                color: K.amberText,
                                padding: compact ? '3px 6px' : '6px 8px',
                                borderRadius: 6, fontSize: compact ? 10 : 11,
                            }}>
                                <span style={{ fontWeight: 700 }}>{t('kds_note')}:</span> {order.notes}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Footer: Done button, Undo bar, or a cancelled marker */}
            {isCancelled ? (
                // No action: cancellation is terminal, and the stamp above
                // already says so. A Done button here would invite a cook to
                // "finish" food the customer has been refunded for.
                <div style={{ height: compact ? 8 : 12 }} />
            ) : isCompleting ? (
                <UndoBar onUndo={onUndo} t={t} />
            ) : (
                <button
                    onClick={onDone}
                    style={{
                        display: 'block', margin: '0 12px 12px', padding: compact ? 8 : 12,
                        background: K.greenDim, color: K.greenText,
                        border: `1px solid ${K.green}44`, borderRadius: 8,
                        fontWeight: 700, fontSize: compact ? 12 : 14,
                        cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.target.style.background = K.green; e.target.style.color = K.greenDark; }}
                    onMouseLeave={e => { e.target.style.background = K.greenDim; e.target.style.color = K.greenText; }}
                >
                    {t('kds_done')}
                </button>
            )}
        </div>
    );
};

// ── Main Component ─────────────────────────────────────────────────
const OrderViewPage = () => {
    const { category } = useParams();
    const navigate = useNavigate();
    const { tenantConfig, getBrandName, getTenantSetting } = useAuth();
    const { t, language, toggleLanguage } = useLanguage();
    const [langDropdownOpen, setLangDropdownOpen] = useState(false);
    const langDropdownRef = useRef(null);

    // Block direct-URL access when there is no active business day
    useBusinessDayGuard();

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
                setLangDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ── State ──
    const [orders, setOrders] = useState([]);
    const [completedOrders, setCompletedOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('active'); // 'active' | 'completed' | 'voided'
    const [voidedOrdersList, setVoidedOrdersList] = useState([]);
    const [soundEnabled, setSoundEnabled] = useState(() => {
        const saved = localStorage.getItem('kds_sound_enabled');
        return saved !== null ? JSON.parse(saved) : true;
    });
    const [completingOrders, setCompletingOrders] = useState({}); // trackingId → timeout
    const [tick, setTick] = useState(0); // forces timer re-render every second
    const [newOrderIds, setNewOrderIds] = useState(new Set()); // for pulse animation
    const [categoryImageUrl, setCategoryImageUrl] = useState(null);
    const [activeSalesDate, setActiveSalesDate] = useState(null);
    const [audioUnlocked, setAudioUnlocked] = useState(false);

    useEffect(() => {
        const loadSalesDate = async () => {
            try {
                const state = await tenantStorage.getBusinessDayState();
                setActiveSalesDate(state.isBusinessDay ? state.salesDate : null);
            } catch (e) {
                console.error('Failed to load sales date:', e);
            }
        };
        loadSalesDate();
    }, []);

    useEffect(() => {
        categoryService.getAll().then(cats => {
            const cat = dedupeByName(cats).find(c => c.name === category);
            if (cat?.image_url) setCategoryImageUrl(cat.image_url);
        }).catch(() => {});
    }, [category]);

    const undoTimeouts = useRef({});
    const playSoundRef = useRef(null);
    const previousOrderIds = useRef(new Set());
    const initialFetchDone = useRef(false);

    // ── Thresholds from tenant config ──
    const thresholds = useMemo(() => {
        const custom = getTenantSetting('kds_timer_thresholds', null);
        if (custom && custom[category]) return custom[category];
        return DEFAULT_THRESHOLDS;
    }, [category, getTenantSetting]);

    // ── Active orders (exclude completing) ──
    // Drives the Active tab COUNT, so cancelled is excluded here — the spec
    // says a cancelled ticket stays visible but stops counting as
    // outstanding work. It is added back into `visibleOrders` below.
    const activeOrders = useMemo(() => {
        return orders.filter(o => o.status !== 'completed'
            && o.status !== 'cancelled'
            && !completingOrders[o.tracking_id]);
    }, [orders, completingOrders]);

    const cancelledOrders = useMemo(() => {
        return orders.filter(o => o.status === 'cancelled'
            && !completingOrders[o.tracking_id]);
    }, [orders, completingOrders]);

    const completingOrdersList = useMemo(() => {
        return orders.filter(o => completingOrders[o.tracking_id]);
    }, [orders, completingOrders]);

    // Cancelled cards render last: they are information, not work queued.
    const visibleOrders = useMemo(() => {
        return [...activeOrders, ...completingOrdersList, ...cancelledOrders];
    }, [activeOrders, completingOrdersList, cancelledOrders]);

    const [windowWidth, setWindowWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const gridCols = useMemo(() => getGridColumns(visibleOrders.length), [visibleOrders.length, windowWidth]);

    // ── Mobile carousel state ──
    const carouselRef = useRef(null);
    const [activeCarouselPage, setActiveCarouselPage] = useState(0);
    const isMobile = windowWidth < 768;

    const handleCarouselScroll = useCallback(() => {
        if (!carouselRef.current) return;
        const el = carouselRef.current;
        const totalPages = Math.ceil(visibleOrders.length / 2);
        if (totalPages <= 0) return;
        const pageWidth = el.scrollWidth / totalPages;
        const page = Math.round(el.scrollLeft / pageWidth);
        setActiveCarouselPage(page);
    }, [visibleOrders.length]);

    // ── Data Fetching ──
    const fetchOrders = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            const fetched = await orderTrackingService.getOrdersByCategory(category, null);
            const newOrders = fetched || [];

            // Detect new orders by ID comparison (works even when an order completes + a new one arrives in the same cycle)
            // 'cancelled' excluded alongside 'completed': a refunded ticket
            // arriving on a poll is not new work and must not ring the
            // new-order chime at the pass.
            const activeIds = new Set(newOrders
                .filter(o => o.status !== 'completed' && o.status !== 'cancelled')
                .map(o => o.tracking_id));
            if (initialFetchDone.current) {
                const brandNew = [...activeIds].filter(id => !previousOrderIds.current.has(id));
                if (brandNew.length > 0) {
                    setNewOrderIds(new Set(brandNew));
                    setTimeout(() => setNewOrderIds(new Set()), 2500);
                    if (playSoundRef.current) playSoundRef.current();
                }
            }
            initialFetchDone.current = true;
            previousOrderIds.current = activeIds;

            setOrders(newOrders);
        } catch (err) {
            console.error(`Error fetching ${category} orders:`, err);
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [category, completingOrders]);

    const fetchCompleted = useCallback(async () => {
        try {
            const fetched = await orderTrackingService.getCompletedOrdersByCategory(category);
            setCompletedOrders(fetched || []);
        } catch (err) {
            console.error(`Error fetching completed orders:`, err);
        }
    }, [category]);

    const fetchVoided = useCallback(async () => {
        // No active sales date = no voided orders to show
        if (!activeSalesDate) {
            setVoidedOrdersList([]);
            return;
        }
        try {
            const salesDay = activeSalesDate.split(' ')[0];
            const data = await voidService.getVoids({
                void_type: 'order_tracking',
                start_date: salesDay + 'T00:00:00',
                end_date: salesDay + 'T23:59:59',
                page_size: 100,
            });
            // Filter to only show voids for this KDS category AND current session's sales_date
            const items = (data.items || []).filter(v => {
                const rd = v.record_data || {};
                return rd.category === category && rd.sales_date === activeSalesDate;
            });
            setVoidedOrdersList(items);
        } catch (err) {
            console.error('Error fetching voided orders:', err);
        }
    }, [category, activeSalesDate]);

    // Initial fetch
    useEffect(() => {
        fetchOrders(true);
        fetchCompleted();
        fetchVoided();
    }, [category, fetchOrders, fetchCompleted, fetchVoided]);

    // Poll every ~10 seconds — primary real-time mechanism (works across devices).
    // Skips while the tab is hidden and adds jitter so multiple terminals don't
    // all hit the backend on the same tick (reduces busy-period load).
    useEffect(() => {
        const interval = setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            fetchOrders(false);
            if (activeTab === 'completed') fetchCompleted();
            if (activeTab === 'voided') fetchVoided();
        }, 10000 + Math.floor(Math.random() * 2000));
        return () => clearInterval(interval);
    }, [fetchOrders, fetchCompleted, fetchVoided, activeTab]);

    // Catch up immediately when page becomes visible again (mobile background, tab switch, screen unlock)
    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                fetchOrders(false);
                if (activeTab === 'completed') fetchCompleted();
                if (activeTab === 'voided') fetchVoided();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [fetchOrders, fetchCompleted, fetchVoided, activeTab]);

    // Tick timer every second for live timers
    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(interval);
    }, []);

    // ── Completion + Undo ──
    const handleDone = useCallback((trackingId) => {
        // Start completing state
        const timeout = setTimeout(async () => {
            // Undo window expired — commit to backend
            try {
                await orderTrackingService.updateOrderStatus(trackingId, 'completed');

                // Dispatch cross-tab event
                const eventData = {
                    type: 'orderStatusChanged',
                    trackingId,
                    category,
                    oldStatus: 'submitted',
                    newStatus: 'completed',
                    timestamp: Date.now(),
                };
                window.dispatchEvent(new CustomEvent('orderEvent', { detail: eventData }));
                localStorage.setItem('orderEvent', JSON.stringify(eventData));
                localStorage.removeItem('orderEvent');

                // Remove from orders list
                setOrders(prev => prev.filter(o => o.tracking_id !== trackingId));
                // Refresh completed tab
                fetchCompleted();
            } catch (err) {
                console.error('Error completing order:', err);
            }

            // Clean up
            setCompletingOrders(prev => {
                const next = { ...prev };
                delete next[trackingId];
                return next;
            });
            delete undoTimeouts.current[trackingId];
        }, 3000);

        undoTimeouts.current[trackingId] = timeout;
        setCompletingOrders(prev => ({ ...prev, [trackingId]: true }));
    }, [category, fetchCompleted]);

    const handleUndo = useCallback((trackingId) => {
        // Cancel the pending completion
        if (undoTimeouts.current[trackingId]) {
            clearTimeout(undoTimeouts.current[trackingId]);
            delete undoTimeouts.current[trackingId];
        }
        setCompletingOrders(prev => {
            const next = { ...prev };
            delete next[trackingId];
            return next;
        });
    }, []);

    // Clean up timeouts on unmount
    useEffect(() => {
        return () => {
            Object.values(undoTimeouts.current).forEach(clearTimeout);
        };
    }, []);

    // ── Sound (cross-browser: Safari, Chrome, Firefox, Electron) ──
    const audioContextRef = useRef(null);

    // Warm up AudioContext on first user interaction (required by Safari/Chrome)
    useEffect(() => {
        const warmUp = async () => {
            try {
                if (!audioContextRef.current) {
                    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                }
                if (audioContextRef.current.state === 'suspended') {
                    await audioContextRef.current.resume();
                }
                setAudioUnlocked(true);
            } catch (e) { /* ignore */ }
        };
        const events = ['click', 'touchstart', 'keydown'];
        events.forEach(ev => document.addEventListener(ev, warmUp, { once: true }));
        return () => events.forEach(ev => document.removeEventListener(ev, warmUp));
    }, []);

    const playNotificationSound = useCallback(async () => {
        if (!soundEnabled) return;
        try {
            // Reuse warmed-up context or create new one
            let ctx = audioContextRef.current;
            if (!ctx || ctx.state === 'closed') {
                ctx = new (window.AudioContext || window.webkitAudioContext)();
                audioContextRef.current = ctx;
            }
            if (ctx.state === 'suspended') await ctx.resume();

            const oscillator = ctx.createOscillator();
            const gain = ctx.createGain();
            oscillator.connect(gain);
            gain.connect(ctx.destination);
            oscillator.frequency.value = 800;
            oscillator.type = 'sine';
            gain.gain.setValueAtTime(0.8, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1.0);
            oscillator.start(ctx.currentTime);
            oscillator.stop(ctx.currentTime + 1.0);
        } catch (err) {
            console.error('AudioContext sound error:', err);
            // Fallback: HTML5 Audio (works on Electron and most browsers)
            try {
                const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmBVDBxOqODowGQcBDaG0fHCdSk=');
                audio.volume = 0.8;
                await audio.play();
            } catch (fallbackErr) {
                // Last resort: vibration (mobile devices)
                if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
            }
        }
    }, [soundEnabled]);

    // Keep ref in sync so fetchOrders can call it without a dependency
    playSoundRef.current = playNotificationSound;

    // ── Real-time via Polling ──
    // No localStorage/CustomEvent — KDS runs on a separate device from the POS.
    // Polling every 5 seconds is the reliable cross-device mechanism.
    // Sound + notification triggered when new orders are detected by the poll.

    // Request notification permission
    useEffect(() => {
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, []);

    // Wake Lock — keep screen on for KDS phones mounted in the kitchen
    const wakeLockRef = useRef(null);
    useEffect(() => {
        const requestWakeLock = async () => {
            try {
                if ('wakeLock' in navigator) {
                    wakeLockRef.current = await navigator.wakeLock.request('screen');
                }
            } catch { /* user denied or unsupported — no-op */ }
        };
        requestWakeLock();
        // Re-acquire after visibility change (wake lock is released when page is hidden)
        const reacquire = () => {
            if (document.visibilityState === 'visible') requestWakeLock();
        };
        document.addEventListener('visibilitychange', reacquire);
        return () => {
            document.removeEventListener('visibilitychange', reacquire);
            if (wakeLockRef.current) wakeLockRef.current.release().catch(() => {});
        };
    }, []);

    const renderSalesDateBanner = () => {
        if (activeSalesDate) {
            return (
                <div style={{
                    background: '#eff5ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: 8,
                    padding: '8px 16px',
                    margin: '12px 20px 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    fontFamily: FONT,
                }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#2563eb' }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#2563eb' }}>
                        {t('sales_date') || 'Sales Date'}
                    </span>
                    <span style={{ fontSize: 13, color: '#475569', fontFamily: 'monospace' }}>
                        {activeSalesDate}
                    </span>
                </div>
            );
        }
        return (
            <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                padding: '8px 16px',
                margin: '12px 20px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                fontFamily: FONT,
            }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#dc2626' }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#dc2626' }}>
                    {t('no_sales_date') || 'No Sales Date'}
                </span>
            </div>
        );
    };

    // ── Redirect if no tenant ──
    if (!tenantConfig || !getBrandName()) {
        return <Navigate to="/functions" replace />;
    }

    // ── RENDER ──
    return (
        <div style={{
            background: K.bg,
            minHeight: windowWidth < 768 ? '100dvh' : '100vh',
            height: windowWidth < 768 ? '100dvh' : '100vh',
            overflow: 'hidden',
            fontFamily: FONT, color: K.text, display: 'flex', flexDirection: 'column',
        }}>
            {/* ── Header ── */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: windowWidth < 768 ? '10px 12px' : '12px 20px',
                background: K.card, borderBottom: `1px solid ${K.border}`,
                flexShrink: 0, flexWrap: 'wrap', gap: windowWidth < 768 ? 8 : 0,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                    {categoryImageUrl && renderEntityImage(categoryImageUrl, windowWidth < 768 ? 22 : 28)}
                    <span style={{ fontWeight: 800, fontSize: windowWidth < 768 ? 16 : 20, whiteSpace: 'nowrap' }}>
                        {translateDynamic(category, t)} {t('Orders')}
                    </span>
                    <div style={{ display: 'flex', gap: 4 }}>
                        {['active', 'completed', 'voided'].map(tab => {
                            const isVoided = tab === 'voided';
                            const count = tab === 'active' ? activeOrders.length : tab === 'completed' ? completedOrders.length : voidedOrdersList.length;
                            const activeColor = isVoided ? K.red : K.blue;
                            const activeBg = isVoided ? '#fee2e2' : K.blueDim;
                            return (
                                <button key={tab} onClick={() => setActiveTab(tab)} style={{
                                    padding: windowWidth < 768 ? '6px 10px' : '8px 16px',
                                    borderRadius: 8, fontSize: windowWidth < 768 ? 12 : 13, fontWeight: 600,
                                    cursor: 'pointer', border: 'none', transition: 'all 0.15s',
                                    background: activeTab === tab ? activeBg : 'transparent',
                                    color: activeTab === tab ? activeColor : K.dimmed,
                                    minHeight: windowWidth < 768 ? 44 : undefined,
                                }}>
                                    {tab === 'active' ? t('kds_active') : tab === 'completed' ? t('kds_completed') : t('voided')}
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        minWidth: 18, height: 18, borderRadius: 10, fontSize: 11,
                                        fontWeight: 700, marginLeft: 4, padding: '0 5px',
                                        background: activeTab === tab ? (isVoided ? '#ef444422' : '#3b82f622') : K.border,
                                        color: activeTab === tab ? activeColor : K.dimmed,
                                    }}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div ref={langDropdownRef} style={{ position: 'relative' }}>
                        <button
                            onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                            style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                padding: 6, borderRadius: 6, display: 'flex',
                                alignItems: 'center', color: K.muted,
                                justifyContent: windowWidth < 768 ? 'center' : 'flex-start',
                                minHeight: windowWidth < 768 ? 44 : undefined,
                                minWidth: windowWidth < 768 ? 44 : undefined,
                            }}
                            title="Language"
                        >
                            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                            </svg>
                        </button>
                        {langDropdownOpen && (
                            <div style={{
                                position: 'absolute', top: '100%', right: 0, marginTop: 4,
                                background: '#fff', border: '1px solid #e2e8f0',
                                borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                minWidth: 140, zIndex: 999, overflow: 'hidden',
                            }}>
                                <button
                                    onClick={() => { if (language !== 'en') toggleLanguage(); setLangDropdownOpen(false); }}
                                    style={{
                                        width: '100%', padding: '8px 14px', border: 'none', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        fontSize: 13, fontWeight: 500, fontFamily: "'Plus Jakarta Sans', sans-serif",
                                        background: language === 'en' ? '#eff5ff' : '#fff',
                                        color: language === 'en' ? '#2563eb' : '#374151',
                                    }}
                                >
                                    English {language === 'en' && <span style={{ color: '#2563eb' }}>&#10003;</span>}
                                </button>
                                <button
                                    onClick={() => { if (language !== 'ar') toggleLanguage(); setLangDropdownOpen(false); }}
                                    style={{
                                        width: '100%', padding: '8px 14px', border: 'none', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        fontSize: 13, fontWeight: 500, fontFamily: "'Plus Jakarta Sans', sans-serif",
                                        background: language === 'ar' ? '#eff5ff' : '#fff',
                                        color: language === 'ar' ? '#2563eb' : '#374151',
                                    }}
                                >
                                    العربية {language === 'ar' && <span style={{ color: '#2563eb' }}>&#10003;</span>}
                                </button>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => {
                            const next = !soundEnabled;
                            setSoundEnabled(next);
                            localStorage.setItem('kds_sound_enabled', JSON.stringify(next));
                        }}
                        style={{
                            width: windowWidth < 768 ? 44 : 36, height: windowWidth < 768 ? 44 : 36, borderRadius: 8,
                            border: `1px solid ${soundEnabled ? K.greenDim : K.border}`,
                            background: 'none', cursor: 'pointer', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', fontSize: 16,
                            color: soundEnabled ? K.greenText : K.muted, transition: 'all 0.15s',
                        }}
                        title={soundEnabled ? t('kds_sound_on') : t('kds_sound_off')}
                    >
                        {soundEnabled ? '\u{1F50A}' : '\u{1F507}'}
                    </button>
                    {soundEnabled && (
                        <button
                            onClick={() => { if (playNotificationSound) playNotificationSound(); }}
                            style={{
                                width: windowWidth < 768 ? 44 : 36, height: windowWidth < 768 ? 44 : 36, borderRadius: 8,
                                border: `1px solid ${K.border}`,
                                background: 'none', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', fontSize: 14,
                                color: K.muted, transition: 'all 0.15s',
                            }}
                            title={t('kds_test_sound') || 'Test sound'}
                        >
                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                            </svg>
                        </button>
                    )}
                    {windowWidth >= 768 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{
                                width: 8, height: 8, borderRadius: '50%', background: K.green,
                                animation: 'kdsPulse 1.5s infinite',
                            }} />
                            <span style={{ fontSize: 11, color: K.dimmed }}>{t('kds_live')}</span>
                        </div>
                    )}
                    <button
                        onClick={() => navigate('/functions')}
                        style={{
                            background: 'none', border: `1.5px solid ${K.border}`, borderRadius: 8,
                            padding: '6px 12px', cursor: 'pointer', color: K.muted,
                            display: 'flex', alignItems: 'center', gap: 6,
                            fontSize: 12, fontWeight: 600, fontFamily: FONT,
                            transition: 'all 0.15s', flexShrink: 0,
                            minHeight: windowWidth < 768 ? 44 : undefined,
                        }}
                        onMouseEnter={e => { e.target.style.background = K.border; }}
                        onMouseLeave={e => { e.target.style.background = 'none'; }}
                    >
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                        {t('Sign Out')}
                    </button>
                </div>
            </div>

            {/* ── Sound Unlock Banner ── */}
            {soundEnabled && !audioUnlocked && (
                <button
                    onClick={async () => {
                        try {
                            if (!audioContextRef.current) {
                                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                            }
                            if (audioContextRef.current.state === 'suspended') {
                                await audioContextRef.current.resume();
                            }
                            setAudioUnlocked(true);
                        } catch { /* ignore */ }
                    }}
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        margin: '0 12px', padding: '10px 16px',
                        background: K.amberDim, border: `1px solid ${K.amberBorder}`,
                        borderRadius: 8, cursor: 'pointer', fontFamily: FONT,
                        fontSize: 13, fontWeight: 600, color: K.amberText,
                        flexShrink: 0,
                    }}
                >
                    {'\u{1F50A}'} {t('kds_tap_to_enable_sound') || 'Tap to enable sound alerts'}
                </button>
            )}

            {/* ── Body ── */}
            <div style={{ flex: 1, padding: windowWidth < 768 ? 10 : 16, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{
                                width: 40, height: 40, border: `3px solid ${K.border}`,
                                borderTopColor: K.green, borderRadius: '50%',
                                animation: 'kdsSpin 0.8s linear infinite', margin: '0 auto 12px',
                            }} />
                            <span style={{ color: K.muted, fontSize: 14 }}>{t('kds_loading_orders')}</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {renderSalesDateBanner()}
                        {activeTab === 'active' ? (
                            visibleOrders.length === 0 ? (
                                /* ── Empty State ── */
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    height: '100%', flexDirection: 'column', gap: 12,
                                }}>
                                    <div style={{ fontSize: 56, opacity: categoryImageUrl ? 1 : 0.5 }}>
                                        {categoryImageUrl ? renderEntityImage(categoryImageUrl, 56) : (category === 'Bar' ? '\u{1F379}' : '\u{1F468}\u{200D}\u{1F373}')}
                                    </div>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: K.muted }}>
                                        {t('kds_no_active_orders')}
                                    </div>
                                    <div style={{ fontSize: 13, color: K.dimmed }}>
                                        {t('kds_orders_appear_here')}
                                    </div>
                                </div>
                            ) : (
                                /* ── Orders Grid / Mobile Carousel ── */
                                isMobile ? (
                                    <>
                                        <style>{`.kds-carousel::-webkit-scrollbar { display: none; }`}</style>
                                        <div
                                            className="kds-carousel"
                                            ref={carouselRef}
                                            onScroll={handleCarouselScroll}
                                            style={{
                                                display: 'flex',
                                                overflowX: 'auto',
                                                overflowY: 'hidden',
                                                scrollSnapType: 'x mandatory',
                                                WebkitOverflowScrolling: 'touch',
                                                gap: 0,
                                                padding: 0,
                                                scrollbarWidth: 'none',
                                                msOverflowStyle: 'none',
                                                flex: 1,
                                            }}
                                        >
                                            {Array.from({ length: Math.ceil(visibleOrders.length / 2) }, (_, pageIdx) => (
                                                <div key={pageIdx} style={{
                                                    scrollSnapAlign: 'start',
                                                    flexShrink: 0,
                                                    width: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 10,
                                                    padding: '0 6px',
                                                    justifyContent: 'flex-start',
                                                }}>
                                                    {visibleOrders.slice(pageIdx * 2, pageIdx * 2 + 2).map(order => (
                                                        <OrderCard
                                                            key={order.tracking_id}
                                                            order={order}
                                                            thresholds={thresholds}
                                                            isNew={newOrderIds.has(order.tracking_id)}
                                                            isCompleting={!!completingOrders[order.tracking_id]}

                                                            onDone={() => handleDone(order.tracking_id)}
                                                            onUndo={() => handleUndo(order.tracking_id)}
                                                            language={language}
                                                            t={t}
                                                            compact={true}
                                                        />
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                        {Math.ceil(visibleOrders.length / 2) > 1 && (
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: '10px 0' }}>
                                                {Array.from({ length: Math.ceil(visibleOrders.length / 2) }, (_, i) => (
                                                    <div key={i} style={{
                                                        width: 8, height: 8, borderRadius: '50%',
                                                        background: i === activeCarouselPage ? '#2563eb' : '#d1d5db',
                                                        transition: 'background 0.2s',
                                                    }} />
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                                        gap: 12,
                                        height: '100%',
                                        alignContent: 'start',
                                    }}>
                                        {visibleOrders.map(order => (
                                            <OrderCard
                                                key={order.tracking_id}
                                                order={order}
                                                thresholds={thresholds}
                                                isNew={newOrderIds.has(order.tracking_id)}
                                                isCompleting={!!completingOrders[order.tracking_id]}
                                                isVoided={!!order._voided}
                                                onDone={() => handleDone(order.tracking_id)}
                                                onUndo={() => handleUndo(order.tracking_id)}
                                                language={language}
                                                t={t}
                                                compact={gridCols >= 5}
                                            />
                                        ))}
                                    </div>
                                )
                            )
                        ) : activeTab === 'completed' ? (
                            /* ── Completed Tab ── */
                            <CompletedTab
                                orders={completedOrders}
                                thresholds={thresholds}
                                t={t}
                                language={language}
                                windowWidth={windowWidth}
                            />
                        ) : (
                            /* ── Voided Tab ── */
                            <VoidedTab
                                voidedOrders={voidedOrdersList}
                                t={t}
                                language={language}
                                windowWidth={windowWidth}
                            />
                        )}
                    </>
                )}
            </div>

            {/* ── Global keyframes ── */}
            <style>{`
                @keyframes kdsPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
                @keyframes kdsSpin { to { transform: rotate(360deg); } }
                @keyframes kdsSlideIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes kdsGlow { 0%, 100% { box-shadow: 0 0 8px ${K.greenDim}; } 50% { box-shadow: 0 0 20px ${K.green}44; } }
                @keyframes kdsFadeOut { to { opacity: 0; transform: scale(0.95); } }
                @keyframes kdsUndoBar { from { width: 100%; } to { width: 0%; } }
            `}</style>
        </div>
    );
};

export default OrderViewPage;
